import {
  MANAGER_TAP_TO_PAY_SETUP_DB_PREFIX,
  saveManagerTapToPaySetupRecordToDB,
  getManagerTapToPaySetupRecordFromDB,
} from 'src/utils/Services/DB_Utils';

export type TapToPaySetupIdentity = {
  businessId?: string | null;
  userId?: string | null;
  /** Apple linking and first-run history are scoped to the connected Stripe account. */
  providerAccountId?: string | null;
};

export const TAP_TO_PAY_SETUP_SCHEMA_VERSION = 3 as const;
export const POINTCRAFT_TAP_TO_PAY_TERMS_VERSION = 1 as const;

export type TapToPaySetupRecord = {
  schemaVersion: typeof TAP_TO_PAY_SETUP_SCHEMA_VERSION;
  businessId: string;
  userId: string;
  providerAccountId: string;
  pointcraftTermsAcceptedAt?: string;
  pointcraftTermsVersion?: number;
  educationCompletedAt?: string;
  guideCompletedAt?: string;
  updatedAt: string;
};

type LegacyTapToPaySetupRecord = {
  pointcraftTermsAcceptedAt?: string;
  educationCompletedAt?: string;
};

export type TapToPaySetupDatabase = {
  read(key: string): Promise<unknown | null>;
  write(key: string, record: TapToPaySetupRecord): Promise<void>;
};

const setupDatabase: TapToPaySetupDatabase = {
  read: (key) => getManagerTapToPaySetupRecordFromDB<unknown>(key),
  write: saveManagerTapToPaySetupRecordToDB,
};

export const LEGACY_TAP_TO_PAY_SETUP_STORAGE_PREFIX = 'pointcraft.manager.tapToPaySetup.v2';

function encodeKeyPart(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? encodeURIComponent(trimmed) : null;
}

function normalizedIdentity(identity: TapToPaySetupIdentity): {
  businessId: string;
  userId: string;
  providerAccountId: string;
} | null {
  const businessId = identity.businessId?.trim();
  const userId = identity.userId?.trim();
  const providerAccountId = identity.providerAccountId?.trim();
  return businessId && userId && providerAccountId
    ? { businessId, userId, providerAccountId }
    : null;
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage ?? null;
}

export function getLegacyTapToPaySetupStorageKey({
  businessId,
  userId,
}: TapToPaySetupIdentity): string | null {
  const businessKey = encodeKeyPart(businessId);
  const userKey = encodeKeyPart(userId);
  return businessKey && userKey
    ? `${LEGACY_TAP_TO_PAY_SETUP_STORAGE_PREFIX}:${businessKey}:${userKey}`
    : null;
}

export function getTapToPaySetupStorageKey(identity: TapToPaySetupIdentity): string | null {
  const normalized = normalizedIdentity(identity);
  if (!normalized) return null;

  return [
    MANAGER_TAP_TO_PAY_SETUP_DB_PREFIX,
    encodeURIComponent(normalized.businessId),
    encodeURIComponent(normalized.userId),
    encodeURIComponent(normalized.providerAccountId),
  ].join(':');
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function parseRecord(identity: TapToPaySetupIdentity, value: unknown): TapToPaySetupRecord | null {
  const normalized = normalizedIdentity(identity);
  if (!normalized || !value || typeof value !== 'object') return null;

  const candidate = value as Partial<TapToPaySetupRecord>;
  if (
    candidate.schemaVersion !== TAP_TO_PAY_SETUP_SCHEMA_VERSION ||
    candidate.businessId !== normalized.businessId ||
    candidate.userId !== normalized.userId ||
    candidate.providerAccountId !== normalized.providerAccountId
  ) {
    return null;
  }

  return {
    schemaVersion: TAP_TO_PAY_SETUP_SCHEMA_VERSION,
    ...normalized,
    ...(optionalString(candidate.pointcraftTermsAcceptedAt)
      ? { pointcraftTermsAcceptedAt: candidate.pointcraftTermsAcceptedAt }
      : {}),
    ...(typeof candidate.pointcraftTermsVersion === 'number'
      ? { pointcraftTermsVersion: candidate.pointcraftTermsVersion }
      : {}),
    ...(optionalString(candidate.educationCompletedAt)
      ? { educationCompletedAt: candidate.educationCompletedAt }
      : {}),
    ...(optionalString(candidate.guideCompletedAt)
      ? { guideCompletedAt: candidate.guideCompletedAt }
      : {}),
    updatedAt: optionalString(candidate.updatedAt) ?? new Date(0).toISOString(),
  };
}

function readLegacyRecord(
  identity: TapToPaySetupIdentity,
  storage: Storage | null
): LegacyTapToPaySetupRecord | null {
  const key = getLegacyTapToPaySetupStorageKey(identity);
  if (!key || !storage) return null;

  try {
    const parsed = JSON.parse(storage.getItem(key) ?? 'null') as LegacyTapToPaySetupRecord | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const pointcraftTermsAcceptedAt = optionalString(parsed.pointcraftTermsAcceptedAt);
    const educationCompletedAt = optionalString(parsed.educationCompletedAt);
    if (!pointcraftTermsAcceptedAt && !educationCompletedAt) return null;
    return {
      ...(pointcraftTermsAcceptedAt ? { pointcraftTermsAcceptedAt } : {}),
      ...(educationCompletedAt ? { educationCompletedAt } : {}),
    };
  } catch {
    return null;
  }
}

function emptyRecord(identity: TapToPaySetupIdentity, now = new Date()): TapToPaySetupRecord {
  const normalized = normalizedIdentity(identity);
  if (!normalized) throw new Error('Tap to Pay setup identity is incomplete');
  return {
    schemaVersion: TAP_TO_PAY_SETUP_SCHEMA_VERSION,
    ...normalized,
    updatedAt: now.toISOString(),
  };
}

export async function readTapToPaySetupRecord(
  identity: TapToPaySetupIdentity,
  database: TapToPaySetupDatabase = setupDatabase,
  legacyStorage = browserStorage()
): Promise<TapToPaySetupRecord | null> {
  const key = getTapToPaySetupStorageKey(identity);
  if (!key) return null;

  const persisted = parseRecord(identity, await database.read(key));
  if (persisted) return persisted;

  const legacy = readLegacyRecord(identity, legacyStorage);
  if (!legacy) return emptyRecord(identity);

  const migrated: TapToPaySetupRecord = {
    ...emptyRecord(identity),
    ...legacy,
    ...(legacy.pointcraftTermsAcceptedAt
      ? { pointcraftTermsVersion: POINTCRAFT_TAP_TO_PAY_TERMS_VERSION }
      : {}),
    // The old education timestamp could only be written after successful
    // Stripe setup, so it is safe evidence that the old first-run guide ran.
    ...(legacy.educationCompletedAt ? { guideCompletedAt: legacy.educationCompletedAt } : {}),
    updatedAt: new Date().toISOString(),
  };
  await database.write(key, migrated);
  legacyStorage?.removeItem(getLegacyTapToPaySetupStorageKey(identity)!);
  return migrated;
}

const writeQueues = new Map<string, Promise<TapToPaySetupRecord>>();

async function updateRecord(
  identity: TapToPaySetupIdentity,
  update: Partial<
    Pick<
      TapToPaySetupRecord,
      | 'pointcraftTermsAcceptedAt'
      | 'pointcraftTermsVersion'
      | 'educationCompletedAt'
      | 'guideCompletedAt'
    >
  >,
  database: TapToPaySetupDatabase = setupDatabase,
  legacyStorage = browserStorage()
): Promise<TapToPaySetupRecord> {
  const key = getTapToPaySetupStorageKey(identity);
  if (!key) throw new Error('Tap to Pay setup identity is incomplete');

  const previous = writeQueues.get(key) ?? Promise.resolve(emptyRecord(identity));
  const next = previous
    .catch(() => emptyRecord(identity))
    .then(async () => {
      const current =
        (await readTapToPaySetupRecord(identity, database, legacyStorage)) ?? emptyRecord(identity);
      const updated: TapToPaySetupRecord = {
        ...current,
        ...update,
        updatedAt: new Date().toISOString(),
      };
      await database.write(key, updated);
      return updated;
    });

  writeQueues.set(key, next);
  const clearQueue = () => {
    if (writeQueues.get(key) === next) writeQueues.delete(key);
  };
  next.then(clearQueue, clearQueue);
  return next;
}

export function recordPointCraftTermsAccepted(
  identity: TapToPaySetupIdentity,
  database: TapToPaySetupDatabase = setupDatabase,
  legacyStorage = browserStorage()
): Promise<TapToPaySetupRecord> {
  return updateRecord(
    identity,
    {
      pointcraftTermsAcceptedAt: new Date().toISOString(),
      pointcraftTermsVersion: POINTCRAFT_TAP_TO_PAY_TERMS_VERSION,
    },
    database,
    legacyStorage
  );
}

export function recordMerchantEducationCompleted(
  identity: TapToPaySetupIdentity,
  database: TapToPaySetupDatabase = setupDatabase,
  legacyStorage = browserStorage()
): Promise<TapToPaySetupRecord> {
  return updateRecord(
    identity,
    { educationCompletedAt: new Date().toISOString() },
    database,
    legacyStorage
  );
}

export function recordTapToPayGuideCompleted(
  identity: TapToPaySetupIdentity,
  database: TapToPaySetupDatabase = setupDatabase,
  legacyStorage = browserStorage()
): Promise<TapToPaySetupRecord> {
  return updateRecord(
    identity,
    { guideCompletedAt: new Date().toISOString() },
    database,
    legacyStorage
  );
}
